import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';

const EMPTY = { raison_sociale: '', nom_contact: '', email: '', telephone: '', adresse: '', ville: '', pays: 'Togo', nif: '', secteur_activite: '', notes: '' };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/clients', { params: { search: search || undefined } });
      setClients(res.data);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    try {
      const res = await api.get(`/clients/${id}`);
      setDetail(res.data);
    } catch { setError('Erreur de chargement du client'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, { ...form, actif: true });
      } else {
        await api.post('/clients', form);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      setDetail(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleEdit = (client) => {
    setForm({
      raison_sociale: client.raison_sociale, nom_contact: client.nom_contact || '',
      email: client.email || '', telephone: client.telephone || '', adresse: client.adresse || '',
      ville: client.ville || '', pays: client.pays || 'Togo', nif: client.nif || '',
      secteur_activite: client.secteur_activite || '', notes: client.notes || '',
    });
    setEditId(client.id);
    setDetail(null);
    setShowForm(true);
  };

  useEscapeClose(showForm, () => setShowForm(false));
  useEscapeClose(detail && !showForm, () => setDetail(null));

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
{error && <div className="gss-alert gss-alert-error">{error}</div>}

      <div className="content-card">
        <div className="gss-toolbar">
          <input className="gss-input" placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Nouveau client
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
      ) : clients.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><rect x="2" y="3" width="20" height="14" rx="2"/>
          </svg>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun client enregistre</div>
          <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
        </div>
      ) : (
        <div className="gss-cards-grid">
          {clients.map(c => (
            <div className="gss-client-card" key={c.id} onClick={() => loadDetail(c.id)}>
              <div className="gss-client-card-header">
                <div className="gss-client-avatar">{c.raison_sociale[0]}</div>
                <div>
                  <h3>{c.raison_sociale}</h3>
                  {c.secteur_activite && <span className="gss-client-sector">{c.secteur_activite}</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={`gss-badge ${c.actif ? 'gss-badge-green' : 'gss-badge-grey'}`}>
                    {c.actif ? 'Actif' : 'Inactif'}
                  </span>
                  <span className="gss-badge" style={{ background: c.statut_commercial === 'prospect' ? '#FFF3E0' : c.statut_commercial === 'ancien_client' ? '#F5F5F5' : '#E8F5E9', color: c.statut_commercial === 'prospect' ? '#E65100' : c.statut_commercial === 'ancien_client' ? '#757575' : '#2E7D32', fontSize: '0.65rem' }}>
                    {c.statut_commercial || 'client'}
                  </span>
                </div>
              </div>
              <div className="gss-client-card-body">
                {c.nom_contact && <div className="gss-client-info"><span>Contact :</span> {c.nom_contact}</div>}
                {c.telephone && <div className="gss-client-info"><span>Tel :</span> {c.telephone}</div>}
                {c.ville && <div className="gss-client-info"><span>Ville :</span> {c.ville}</div>}
                <div className="gss-client-info"><span>Contrats actifs :</span> {c.contrats_actifs || 0}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <div className="gss-modal-overlay" onClick={() => setDetail(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>{detail.raison_sociale}</h2>
              <button className="gss-modal-close" onClick={() => setDetail(null)}>&times;</button>
            </div>
            <div className="gss-detail-grid">
              <div className="gss-detail-item"><label>Contact</label><span>{detail.nom_contact || '-'}</span></div>
              <div className="gss-detail-item"><label>Email</label><span>{detail.email || '-'}</span></div>
              <div className="gss-detail-item"><label>Telephone</label><span>{detail.telephone || '-'}</span></div>
              <div className="gss-detail-item"><label>Ville</label><span>{detail.ville || '-'}</span></div>
              <div className="gss-detail-item"><label>NIF</label><span>{detail.nif || '-'}</span></div>
              <div className="gss-detail-item"><label>Secteur</label><span>{detail.secteur_activite || '-'}</span></div>
            </div>

            <div className="content-card-title" style={{ marginTop: '1.5rem' }}>Contrats</div>
            {detail.contrats && detail.contrats.length > 0 ? (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead>
                    <tr><th>Numero</th><th>Intitule</th><th>Debut</th><th>Fin</th><th>Montant/mois</th><th>Statut</th></tr>
                  </thead>
                  <tbody>
                    {detail.contrats.map(ct => (
                      <tr key={ct.id}>
                        <td><strong>{ct.numero}</strong></td>
                        <td>{ct.intitule}</td>
                        <td>{ct.date_debut?.split('T')[0]}</td>
                        <td>{ct.date_fin?.split('T')[0] || '-'}</td>
                        <td>{ct.montant_mensuel ? Number(ct.montant_mensuel).toLocaleString() + ' FCFA' : '-'}</td>
                        <td>
                          <span className={`gss-badge ${ct.statut === 'actif' ? 'gss-badge-green' : 'gss-badge-grey'}`}>
                            {ct.statut}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>Aucun contrat</p>
            )}

            <div style={{ padding: '0 1.5rem 1rem' }}>
              <IAAnalysisPanel type="client" buttonLabel="Score satisfaction IA" data={{ client_id: detail.id }} />
            </div>

            <div className="gss-modal-actions">
              <button className="gss-btn" onClick={() => setDetail(null)}>Fermer</button>
              <button className="gss-btn gss-btn-primary" onClick={() => handleEdit(detail)}>Modifier</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  {editId ? 'Modifier le client' : 'Nouveau client'}
                </h2>
                <div className="gss-modal-subtitle">Gestion du portefeuille client</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="gss-form-grid">
                <div className="gss-modal-section">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
                  <span>IDENTIFICATION CLIENT</span>
                </div>
                <div className="gss-field gss-field-full">
                  <label>Raison sociale *</label>
                  <input className="gss-input" required value={form.raison_sociale} onChange={e => setField('raison_sociale', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Nom du contact</label>
                  <input className="gss-input" value={form.nom_contact} onChange={e => setField('nom_contact', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Email</label>
                  <input className="gss-input" type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
                </div>
                <div className="gss-modal-section">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72"/></svg>
                  <span>COORDONNEES</span>
                </div>
                <div className="gss-field">
                  <label>Telephone</label>
                  <input className="gss-input" value={form.telephone} onChange={e => setField('telephone', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Ville</label>
                  <input className="gss-input" value={form.ville} onChange={e => setField('ville', e.target.value)} />
                </div>
                <div className="gss-field gss-field-full">
                  <label>Adresse</label>
                  <input className="gss-input" value={form.adresse} onChange={e => setField('adresse', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>NIF</label>
                  <input className="gss-input" value={form.nif} onChange={e => setField('nif', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Secteur d'activite</label>
                  <input className="gss-input" value={form.secteur_activite} onChange={e => setField('secteur_activite', e.target.value)} />
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      {editId ? 'Mettre a jour' : 'Enregistrer le client'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
